<template>
    <div
        class="-m-1 max-h-[48rem] overflow-y-auto p-1"
        data-test="file-grid"
    >
        <!-- About four rows, then the grid scrolls inside itself. The padding keeps card edges and focus outlines clear of the clip. -->
        <div class="grid grid-cols-[repeat(auto-fill,minmax(9rem,1fr))] gap-3">
            <FileCard
                v-for="file in files"
                :key="file.id"
                :file="file"
                :label="getLabel(file)"
                :can-download="canDownload(file)"
                :can-edit="canEdit(file)"
                :can-delete="canDelete(file)"
                :thumbnail-failed="failedIds.has(file.id)"
                :original-thumbnails="originalThumbnails"
                :resolve-thumbnail-url="resolveThumbnailUrl"
                @open="emit('open', file)"
                @download="emit('download', file)"
                @edit="emit('edit', file)"
                @delete="emit('delete', file)"
                @image-error="onImageError(file)"
            >
                <template
                    v-if="$slots['menu-items']"
                    #menu-items="{ run }"
                >
                    <slot
                        name="menu-items"
                        :file="file"
                        :run="run"
                    />
                </template>
                <template
                    v-if="$slots.chips"
                    #chips
                >
                    <slot
                        name="chips"
                        :file="file"
                    />
                </template>
            </FileCard>
        </div>
        <!-- Inside the scroller, so whatever follows the last card (a load-more sentinel) scrolls with the cards. -->
        <slot name="footer" />
    </div>
</template>

<script setup lang="ts">
import FileCard from '@components/files/FileCard.vue';
import { getFileDisplayName, thumbnailSource } from '@utilities/fileDisplay';

/**
 * A file list's thumbnail view: the caller's own array as cards, so an edit or
 * delete lands in the list view too. Fetches nothing; `refresh` asks the caller
 * to refetch when a thumbnail's URL may have gone stale.
 */

type FileRecord = Record<string, any>;
type FileId = string | number;

const props = withDefaults(
    defineProps<{
        files: FileRecord[];
        getLabel?: (file: FileRecord) => string;
        canEdit?: (file: FileRecord) => boolean;
        canDelete?: (file: FileRecord) => boolean;
        canDownload?: (file: FileRecord) => boolean;
        /** False where `url` points at a private object, so thumbnails never try it. */
        originalThumbnails?: boolean;
        /** With `originalThumbnails` false: signs a link for each image card (the caller's request). */
        resolveThumbnailUrl?: (file: FileRecord) => Promise<string>;
    }>(),
    {
        getLabel: getFileDisplayName,
        canEdit: () => false,
        canDelete: () => false,
        canDownload: () => true,
        originalThumbnails: true,
        resolveThumbnailUrl: undefined,
    },
);

const emit = defineEmits<{
    open: [file: FileRecord];
    download: [file: FileRecord];
    edit: [file: FileRecord];
    delete: [file: FileRecord];
    /** A thumbnail failed to load, or one is still being built; refetch the list. */
    refresh: [];
}>();

const thumbnailSrc = (file: FileRecord) => {
    const source = thumbnailSource(file, Date.now(), props.originalThumbnails);

    return 'src' in source ? source.src : '';
};

/*
 * A failing thumbnail is hidden at once, so a broken image never shows, and only comes back if a
 * refetch brings a different URL. Each signed one gets one refetch, and a burst of them shares it.
 */
const REFRESH_BATCH_MS = 300;

const failedIds = ref(new Set<FileId>());
const refreshedIds = new Set<FileId>();
const failedSrc = new Map<FileId, string>();
let refreshTimer: ReturnType<typeof setTimeout> | undefined;

const requestRefresh = () => {
    refreshTimer ??= setTimeout(() => {
        refreshTimer = undefined;
        emit('refresh');
    }, REFRESH_BATCH_MS);
};

const onImageError = (file: FileRecord) => {
    if (failedIds.value.has(file.id)) return;

    failedIds.value = new Set(failedIds.value).add(file.id);
    failedSrc.set(file.id, thumbnailSrc(file));

    // A refetch can't help an original (its `url` is unsigned and never changes) or a card that signs its own link.
    if (refreshedIds.has(file.id) || !file.thumb_url) return;

    refreshedIds.add(file.id);
    requestRefresh();
};

// A failed card comes back once the list brings it a different source: a fresh URL, or its real thumbnail arriving.
watch(
    () => props.files,
    (files) => {
        if (!failedSrc.size) return;

        const failed = new Set(failedIds.value);

        failedSrc.forEach((src, id) => {
            const file = files.find((item) => item.id === id);

            if (file && thumbnailSrc(file) !== src) {
                failed.delete(id);
                failedSrc.delete(id);
            }
        });

        failedIds.value = failed;
    },
);

onBeforeUnmount(() => clearTimeout(refreshTimer));

// Lists with no live channel pick a finished thumbnail up when this view is shown.
onMounted(() => {
    if (props.files.some((file) => thumbnailSource(file, Date.now(), props.originalThumbnails).kind === 'pending')) {
        emit('refresh');
    }
});
</script>
